import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { Button, Typography } from 'ui';
import { Form, Formik } from 'formik';
import { FunctionComponent, useEffect } from 'react';
import {
  GridWidgetFields,
  GridWidgetFormFields,
  validateGridWidgetFormFields,
} from 'components/consumer-page-widgets/grid-widget/GridWidgetFormFields';
import { StoreType } from 'utils/connect';
import { Widgets } from '@tock/shared/types/widget';
import { consumer } from '@tock/coretypes';
import {
  getBaseFormFieldsInitialValues,
  useCreateNamedConfigurations,
  useFetchAllCollectionConfigurations,
} from 'components/consumer-page-widgets/widgetFormUtils';
import { useSelector } from 'react-redux';
import {
  widgetFormButtonContainer,
  widgetFormRoot,
} from 'components/consumer-page-widgets/widgetFormCssDefs';

interface CreateShopHeroWidgetFormProps {
  onCompleteOrCancel(): void;
  displayMode: consumer.GridWidgetDisplayType;
}

export const CreateGridWidgetForm: FunctionComponent<
  React.PropsWithChildren<CreateShopHeroWidgetFormProps>
> = ({ onCompleteOrCancel, displayMode }) => {
  const createNamedConfig = useCreateNamedConfigurations();
  const fetchAllCollectionConfigurations = useFetchAllCollectionConfigurations();

  const activeCollectionConfigs = useSelector(
    (state: StoreType) => state.consumerPage.collectionDataConfigurations
  ) as consumer.CollectionDataConfiguration[];

  useEffect(() => {
    fetchAllCollectionConfigurations();
  }, [fetchAllCollectionConfigurations]);

  const getGridDataConfig = ({ dataType, configId, pageSize }: GridWidgetFields) => {
    switch (dataType) {
      case 'business':
        return {
          businessConfig: {
            dataWidgetConfigId: configId,
            searchConfigParams: {
              size: pageSize,
            },
          },
        };
      case 'experience':
        return {
          experienceConfig: {
            dataWidgetConfigId: configId,
            searchConfigParams: {
              size: pageSize,
            },
          },
        };

      default:
        return {};
    }
  };

  const handleSubmit = (values: GridWidgetFields) => {
    const { title, name, subtitle, linkText, linkHref } = values;
    const config: consumer.INamedConfiguration = {
      name,
      widgetType: Widgets.WidgetCreatorWidgetType,
      gridWidgetConfig: {
        title,
        subtitle,
        linkText,
        linkHref,
        displayType: displayMode,
        ...getGridDataConfig(values),
      },
    };
    return createNamedConfig(config).then(onCompleteOrCancel);
  };

  return (
    <>
      <Typography variant="heading1Bold">Create widget</Typography>
      <Formik<GridWidgetFields>
        enableReinitialize
        initialValues={{
          ...getBaseFormFieldsInitialValues(),
          title: '',
          subtitle: '',
          linkHref: '',
          linkText: '',
          dataType: '',
          configId: -1,
          pageSize: 25,
        }}
        onSubmit={handleSubmit}
        validate={validateGridWidgetFormFields}
      >
        <Form css={widgetFormRoot}>
          <BaseWidgetFormFields />
          <GridWidgetFormFields
            collectionDataConfigurations={activeCollectionConfigs}
            displayMode={displayMode}
          />
          <div css={widgetFormButtonContainer}>
            <Button type="submit">Submit</Button>
            <Button variant="outlined" type="button" onClick={onCompleteOrCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Formik>
    </>
  );
};
